import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { CarrierService } from 'src/app/management/service/carrier.service';
import { SessionStorageService } from '../../common/service/session-storage.service';
import { TenantInfo } from '../model/tenant-info';
import { QualificationService } from './qualifications.service';
import { ReviewLabelService } from './review-label.service';
import { UserService } from './user.service';
import { ViolationService } from './violations.service';
import { ContractService } from './contracts.service';

@Injectable()
export class TenantService {

    private readonly STORAGE_NAME = 'sakp-tenant';
    private tenantInfo = new BehaviorSubject<TenantInfo>(null);
    tenantChange$ = this.tenantInfo.asObservable();
    private readonly availableTenants$ = new Subject<TenantInfo[]>();
    private availableTenants: TenantInfo[] | null = null;

    constructor(private sessionStorageService: SessionStorageService,
        private carrierService: CarrierService,
        private contractService: ContractService,
        private violationService: ViolationService,
        private qualificationService: QualificationService,
        private reviewLabelsService: ReviewLabelService,
        private userService: UserService,
        private router: Router
    ) {
        // You may need to adjust this depending on your application's flow
        // The original logic here was a bit unusual, so this is a simplification.
        const tenant = this.sessionStorageService.getItem(this.STORAGE_NAME, TenantInfo);
        this.setStorageTenantInfo(tenant);
        // load available tenants
        this.carrierService.fetchCarriers().pipe(
            map(carriers => carriers.map(c => new TenantInfo(c.id, c.name)))
        ).subscribe(ts => {
            this.availableTenants$.next(ts);
            this.availableTenants = ts;
        });
    }

    getAvailableTenants(): Observable<TenantInfo[]> {
        if (this.availableTenants != null)
            return of(this.availableTenants);
        else
            return this.availableTenants$.pipe(take(1));
    }

    setTenant(tenant: TenantInfo): Observable<void> {
        return this.handleSelectTenant(tenant).pipe(
            tap(() => this.setStorageTenantInfo(tenant)),
            catchError(err => {
                this.handleTenantSelectionError(err);
                throw err;
            })
        );
    }

    setTenantById(idTenant: number): Observable<void> {
        return this.getAvailableTenants().
            pipe(
                map(ts => ts.find(t => t.id === idTenant) ?? null),
                tap(t => { if (t == null) throw new Error('not found available tenant'); }),
                switchMap(t => this.setTenant(t))
            );
    }

    private setStorageTenantInfo(tenantInfo: TenantInfo): void {
        this.tenantInfo.next(tenantInfo);
        this.sessionStorageService.setItem(this.STORAGE_NAME, tenantInfo);
    }

    getTenant(): TenantInfo | null {
        return this.tenantInfo.getValue();
    }

    getTenantId(): number | null {
        return this.getTenant().id;
    }

    clearTenant(): void {
        this.tenantInfo.next(null);
    }

    isTenantSelected(): boolean {
        return this.tenantInfo.getValue() != null;
    }

    isTenantSelected$(): Observable<boolean> {
        return this.tenantInfo.asObservable().pipe(
            map(t => t != null)
        );
    }

    private handleSelectTenant(tenantInfo: TenantInfo): Observable<any> {
        if (tenantInfo != null) {
            return forkJoin([
                this.contractService.initiate(tenantInfo).pipe(take(1)),
                this.qualificationService.initiate(tenantInfo).pipe(take(1)),
                this.violationService.initiate(tenantInfo).pipe(take(1)),
                this.reviewLabelsService.initiate(tenantInfo).pipe(take(1)),
                this.userService.selectByTenant(tenantInfo).pipe(take(1))
            ]);
        }
        return of(null);
    }

    private handleTenantSelectionError(_err: any): void {
        this.clearTenant();
        this.contractService.clear();
        this.violationService.clear();
        this.qualificationService.clear();
        this.reviewLabelsService.clear();
        this.router.navigate(['/503']).catch(e => console.log(e));
    }

}
